import { useCallback, useEffect, useRef, useState } from 'react';
import { notify } from '../../../notifications';
import { mathErrorMessage } from '../report/mathReportApi';
import { getCurrentMonth, parseMonth } from '../report/mathReportCalc';
import { fetchMathChanges, markAllMathChecked, markMathChecked } from './changesApi';
import type { MathChange } from './changesApi';

/** Данные вкладки «Мат. отчет → Изменения»: список за месяц и отметки «Проверено» */
export function useMathChanges() {
  const [month, setMonth] = useState(getCurrentMonth);
  const [rows, setRows] = useState<MathChange[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markingAll, setMarkingAll] = useState(false);
  const [markingIds, setMarkingIds] = useState<Record<number, boolean>>({});

  const requestRef = useRef(0);
  const markingAllRef = useRef(false);
  const markingRef = useRef<Record<number, boolean>>({});

  /** keepRows: после действия на экране остаются прежние строки (приглушенные), а не пустой экран */
  const load = useCallback(
    async (keepRows: boolean) => {
      const requestId = requestRef.current + 1;
      requestRef.current = requestId;
      setLoading(true);
      setError('');
      if (!keepRows) setRows(null);

      try {
        const list = await fetchMathChanges(parseMonth(month));
        // Устаревший ответ (месяц уже сменили или экран закрыт) отбрасывается
        if (requestId !== requestRef.current) return;
        setRows(list);
      } catch (err) {
        if (requestId !== requestRef.current) return;
        const reason = mathErrorMessage(err, 'Проверьте соединение и повторите попытку');
        if (keepRows) notify(`Не удалось обновить список. ${reason}`, 'error');
        else setError(reason);
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [month]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  useEffect(
    () => () => {
      requestRef.current += 1;
    },
    []
  );

  const retry = useCallback(() => {
    load(false);
  }, [load]);

  const markChecked = useCallback(async (id: number) => {
    // Повторный щелчок, пока запрос по этой записи идет, и отметка во время «Отметить всё» игнорируются
    if (markingAllRef.current || markingRef.current[id]) return;
    markingRef.current[id] = true;
    setMarkingIds((prev) => ({ ...prev, [id]: true }));

    try {
      await markMathChecked(id);
      setRows((prev) => prev && prev.filter((item) => item.id !== id));
      notify('Запись отмечена проверенной', 'success');
    } catch (err) {
      notify(mathErrorMessage(err, 'Не удалось отметить запись'), 'error');
    } finally {
      delete markingRef.current[id];
      setMarkingIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }, []);

  /** Снимает пометки у всех записей месяца; список всегда перечитывается, даже после сбоя связи */
  const markAll = useCallback(async () => {
    if (markingAllRef.current || Object.keys(markingRef.current).length > 0) return;
    markingAllRef.current = true;
    setMarkingAll(true);

    try {
      await markAllMathChecked(parseMonth(month));
      notify('Все изменения за месяц отмечены проверенными', 'success');
    } catch (err) {
      notify(mathErrorMessage(err, 'Не удалось отметить все записи'), 'error');
    }

    try {
      await load(true);
    } finally {
      markingAllRef.current = false;
      setMarkingAll(false);
    }
  }, [load, month]);

  return { month, setMonth, rows, loading, error, retry, markingIds, markingAll, markChecked, markAll };
}
